import { usePageMeta } from "@/lib/seo";
import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { openRazorpay } from "@/lib/razorpay";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { MessageCircle, RefreshCw, NotebookPen, Sparkles, ArrowRight, Moon } from "lucide-react";

const CARDS = [
  { icon: MessageCircle, n: "01", title: "Trigger line", desc: "Message me when the old pattern shows up. You get a grounded, personal reply within 24 hours on what to do next." },
  { icon: RefreshCw, n: "02", title: "Weekly reset", desc: "One short practice every week: a prompt, a grounding technique or a reframe. Five minutes, no overwhelm." },
  { icon: NotebookPen, n: "03", title: "Pattern notes", desc: "A simple way to catch your loop: what triggered it, the story you told yourself, and the choice you made instead." },
  { icon: Sparkles, n: "04", title: "Quick-win toolkit", desc: "Scripts, journaling prompts and tiny rituals you can use the same day you receive them." },
];

const FAQ = [
  { q: "Who is this for?", a: "Women who've taken a session with me — a reading, workshop or masterclass — and want support while the shift settles into daily life. If you haven't had a session yet, book one first; the Circle works best with that foundation." },
  { q: "How do I reach you?", a: "Through the trigger line on WhatsApp. Message me when the old pattern shows up and I reply personally within 24 hours — usually much sooner." },
  { q: "Can I switch from 15 days to a month?", a: "Yes, anytime during your 15 days — just message me and pay the ₹1,000 difference. Your days roll over, nothing is lost." },
  { q: "What if I miss a week?", a: "Nothing bad. Missed weeks are rest stops, not failures. The practices wait for you, and your trigger line stays open for your whole plan." },
];

export default function InnerCircle() {
  usePageMeta("The Inner Circle — Just be you by Mehjabin", "Support for the exact moment the old pattern shows up. Insight fades in a trigger. Practice doesn't. 15 seats only.");
  const [status, setStatus] = useState(null);
  const [plan, setPlan] = useState("1-month");
  const [form, setForm] = useState({ name: "", email: "", whatsapp: "", session_taken: "", consent: false });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [wl, setWl] = useState({ name: "", email: "", whatsapp: "" });
  const [wlDone, setWlDone] = useState(false);
  const plansRef = useRef(null);
  const formRef = useRef(null);

  const loadStatus = () => api.get("/circle/status").then((r) => setStatus(r.data));
  useEffect(() => { loadStatus(); }, []);

  const full = status && status.seats_left <= 0;
  const scrollTo = (ref) => ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  const pickPlan = (p) => { setPlan(p); setTimeout(() => scrollTo(formRef), 60); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) { toast.error("Please tick the understanding checkbox first 💛"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/circle/join", { ...form, plan });
      openRazorpay(data, {
        onSuccess: () => { setDone(true); setSubmitting(false); loadStatus(); window.scrollTo({ top: 0, behavior: "smooth" }); },
        onFailure: (dismissed) => {
          toast.error(dismissed ? "Payment wasn't completed — your details are saved below, just press Reserve again." : "Payment failed. Please try again — your details are kept.");
          setSubmitting(false);
        },
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something interrupted that. Please try again.");
      setSubmitting(false);
      loadStatus();
    }
  };

  const joinWaitlist = async (e) => {
    e.preventDefault();
    await api.post("/circle/waitlist", wl);
    setWlDone(true);
  };

  if (done) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-6" data-testid="circle-thankyou">
        <div className="max-w-xl text-center py-24">
          <Moon size={34} strokeWidth={1.3} className="mx-auto text-lapis mb-8" />
          <h1 className="font-serif font-light text-4xl sm:text-5xl text-ink leading-tight">Your seat is <span className="italic text-lapis">reserved.</span></h1>
          <p className="mt-7 text-ink/70 leading-relaxed">I'll message you on WhatsApp within 24 hours with your welcome pack and how to reach the trigger line. A confirmation email is already on its way to you. 💛</p>
          <p className="mt-4 text-sm text-ink/55">This round begins {status?.round_label}.</p>
          <Link to="/" className="btn-primary mt-10 inline-flex" data-testid="circle-thankyou-home">Back home <ArrowRight size={15} /></Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="circle-page">
      {/* HERO */}
      <section className="relative pt-16 md:pt-24 pb-16 overflow-hidden">
        <div className="absolute -top-24 right-[-10%] w-[480px] h-[480px] bg-peach/25 organic-blob blur-3xl" />
        <div className="absolute top-40 left-[-8%] w-[380px] h-[380px] bg-sage/20 organic-blob blur-3xl" />
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-peach/25 border border-peach-deep/30 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.24em] text-peach-deep mb-8" data-testid="circle-seats-badge">
            <Moon size={12} /> {status ? (full ? "This round is full" : `Only ${status.seats_left} of ${status.seats_total} seats left`) : "Only 15 seats"}
          </div>
          <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.08] tracking-tight text-ink">
            You've done the sessions.<br /><span className="italic text-lapis">Now keep the shift.</span>
          </h1>
          <p className="mt-7 max-w-xl mx-auto text-ink/65 leading-relaxed">
            The Inner Circle is support for the exact moment the old pattern shows up. Insight fades in a trigger. Practice doesn't.
          </p>
          <button onClick={() => scrollTo(plansRef)} className="btn-primary mt-9 inline-flex" data-testid="circle-hero-cta">
            Reserve your seat <ArrowRight size={16} />
          </button>
        </div>
      </section>

      {/* WHAT'S INSIDE */}
      <section className="max-w-5xl mx-auto px-6 pb-20">
        <div className="overline text-center mb-10">What's inside</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {CARDS.map(({ icon: Icon, n, title, desc }, i) => (
            <motion.div key={n} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.08 }}
              className="rounded-[1.8rem] bg-white/70 border border-lapis/10 p-8" data-testid={`circle-card-${n}`}>
              <div className="flex items-center gap-4 mb-4">
                <span className="font-serif italic text-2xl text-peach-deep">{n}</span>
                <Icon size={22} strokeWidth={1.5} className="text-lapis" />
              </div>
              <h3 className="font-serif text-2xl text-ink">{title}</h3>
              <p className="mt-3 text-sm text-ink/65 leading-relaxed">{desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS + BUILDING */}
      <section className="max-w-5xl mx-auto px-6 pb-20">
        <div className="overline mb-8">How it works</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
          {[["Join", "Reserve one of 15 seats and choose 15 days or a full month."], ["Practise", "Do the weekly reset and log your triggers as they happen."], ["Reach out", "When a trigger hits, message me and get support in the moment."]].map(([t, d]) => (
            <div key={t} data-testid={`circle-step-${t.toLowerCase().replace(/\s/g, "-")}`}>
              <h3 className="font-serif italic text-2xl text-lapis">{t}</h3>
              <p className="mt-2 text-sm text-ink/65 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
        <div className="mt-14 rounded-[2rem] bg-lapis text-canvas px-8 md:px-12 py-10" data-testid="circle-building">
          <div className="text-[11px] font-semibold uppercase tracking-[0.26em] text-peach mb-4">What you're building</div>
          <p className="font-serif font-light text-2xl md:text-3xl leading-snug">
            Notice the pattern as it starts. Pause before the old reaction. <span className="italic text-peach">Choose one aligned action.</span>
          </p>
        </div>
      </section>

      {/* PLANS */}
      <section ref={plansRef} className="max-w-4xl mx-auto px-6 pb-16 scroll-mt-24">
        <div className="overline text-center mb-10">Choose your plan · round begins {status?.round_label || "1st November"}</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <button onClick={() => !full && pickPlan("15-days")} disabled={full}
            className={`text-left rounded-[2rem] border-2 p-8 transition-all duration-300 ${plan === "15-days" ? "border-lapis bg-white shadow-lg" : "border-lapis/15 bg-white/60 hover:border-lapis/40"} disabled:opacity-50`}
            data-testid="circle-plan-15-days">
            <div className="overline mb-3">15 days</div>
            <div className="font-serif text-5xl text-ink">₹1,500</div>
            <p className="mt-3 text-sm text-ink/65">A short reset to get back on track.</p>
          </button>
          <button onClick={() => !full && pickPlan("1-month")} disabled={full}
            className={`relative text-left rounded-[2rem] border-2 p-8 transition-all duration-300 ${plan === "1-month" ? "border-lapis bg-lapis text-canvas shadow-xl" : "border-lapis/15 bg-white/60 hover:border-lapis/40"} disabled:opacity-50`}
            data-testid="circle-plan-1-month">
            <span className="absolute -top-3 right-6 bg-peach-deep text-canvas text-[10px] font-bold uppercase tracking-[0.18em] rounded-full px-3.5 py-1.5">Best value</span>
            <div className={`overline mb-3 ${plan === "1-month" ? "!text-peach" : ""}`}>1 month</div>
            <div className="font-serif text-5xl">₹2,500</div>
            <p className={`mt-3 text-sm ${plan === "1-month" ? "text-canvas/75" : "text-ink/65"}`}>A full month of support to make it stick.</p>
          </button>
        </div>
      </section>

      {/* FORM / FULL */}
      <section ref={formRef} className="max-w-2xl mx-auto px-6 pb-20 scroll-mt-24">
        {full ? (
          <div className="rounded-[2rem] bg-white/70 border border-lapis/15 p-8 md:p-10 text-center" data-testid="circle-full-waitlist">
            <h2 className="font-serif text-3xl text-ink">This round is <span className="italic text-peach-deep">full.</span></h2>
            {wlDone ? (
              <p className="mt-5 text-ink/70">You're on the waitlist, love — you'll hear from me first when a seat opens. 💛</p>
            ) : (
              <>
                <p className="mt-4 text-sm text-ink/65">Join the waitlist and you'll be the first to know when a seat opens.</p>
                <form onSubmit={joinWaitlist} className="mt-7 space-y-4 text-left">
                  <div><label className="block text-xs font-semibold uppercase tracking-[0.18em] text-lapis mb-1.5" htmlFor="wl-name">Full name</label>
                    <input id="wl-name" required className="w-full rounded-xl border border-lapis/20 bg-white px-4 py-3 text-ink" value={wl.name} onChange={(e) => setWl({ ...wl, name: e.target.value })} data-testid="waitlist-name-input" /></div>
                  <div><label className="block text-xs font-semibold uppercase tracking-[0.18em] text-lapis mb-1.5" htmlFor="wl-email">Email</label>
                    <input id="wl-email" required type="email" className="w-full rounded-xl border border-lapis/20 bg-white px-4 py-3 text-ink" value={wl.email} onChange={(e) => setWl({ ...wl, email: e.target.value })} data-testid="waitlist-email-input" /></div>
                  <div><label className="block text-xs font-semibold uppercase tracking-[0.18em] text-lapis mb-1.5" htmlFor="wl-wa">WhatsApp (with country code)</label>
                    <input id="wl-wa" required className="w-full rounded-xl border border-lapis/20 bg-white px-4 py-3 text-ink" placeholder="+91…" value={wl.whatsapp} onChange={(e) => setWl({ ...wl, whatsapp: e.target.value })} data-testid="waitlist-whatsapp-input" /></div>
                  <button type="submit" className="btn-primary w-full justify-center" data-testid="waitlist-submit-btn">Join the waitlist</button>
                </form>
              </>
            )}
          </div>
        ) : (
          <form onSubmit={submit} className="rounded-[2rem] bg-white/70 border border-lapis/15 p-8 md:p-10 space-y-5" data-testid="circle-form">
            <h2 className="font-serif text-3xl text-ink">Book your <span className="italic text-lapis">seat</span></h2>
            <p className="text-sm text-ink/60 -mt-2">Selected: <strong className="text-lapis">{plan === "15-days" ? "15 Days · ₹1,500" : "1 Month · ₹2,500"}</strong></p>
            <div><label className="block text-xs font-semibold uppercase tracking-[0.18em] text-lapis mb-1.5" htmlFor="c-name">Full name</label>
              <input id="c-name" required className="w-full rounded-xl border border-lapis/20 bg-white px-4 py-3 text-ink" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="circle-name-input" /></div>
            <div><label className="block text-xs font-semibold uppercase tracking-[0.18em] text-lapis mb-1.5" htmlFor="c-email">Email</label>
              <input id="c-email" required type="email" className="w-full rounded-xl border border-lapis/20 bg-white px-4 py-3 text-ink" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="circle-email-input" /></div>
            <div><label className="block text-xs font-semibold uppercase tracking-[0.18em] text-lapis mb-1.5" htmlFor="c-wa">WhatsApp number (with country code)</label>
              <input id="c-wa" required className="w-full rounded-xl border border-lapis/20 bg-white px-4 py-3 text-ink" placeholder="+91 98765 43210" value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} data-testid="circle-whatsapp-input" /></div>
            <div><label className="block text-xs font-semibold uppercase tracking-[0.18em] text-lapis mb-1.5" htmlFor="c-session">Which session did you take with me? (optional)</label>
              <input id="c-session" className="w-full rounded-xl border border-lapis/20 bg-white px-4 py-3 text-ink" placeholder="e.g. tarot reading in October" value={form.session_taken} onChange={(e) => setForm({ ...form, session_taken: e.target.value })} data-testid="circle-session-input" /></div>
            <label className="flex items-start gap-3 text-sm text-ink/70 cursor-pointer">
              <input type="checkbox" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} className="mt-1 h-4 w-4 accent-[#284b63]" data-testid="circle-consent-checkbox" />
              <span>I understand this is a support and practice space, not a replacement for medical or mental health care.</span>
            </label>
            <button type="submit" disabled={submitting} className="btn-primary w-full justify-center disabled:opacity-60" data-testid="circle-reserve-btn">
              {submitting ? "Opening secure checkout…" : `Reserve your seat · ₹${plan === "15-days" ? "1,500" : "2,500"}`} <ArrowRight size={15} />
            </button>
            <p className="text-center text-xs text-ink/50">Secure payment via Razorpay · UPI, cards & netbanking</p>
          </form>
        )}
      </section>

      {/* FAQ */}
      <section className="max-w-2xl mx-auto px-6 pb-24">
        <div className="overline text-center mb-8">Gentle questions</div>
        <div className="space-y-3">
          {FAQ.map((f, i) => (
            <details key={i} className="group rounded-2xl bg-white/60 border border-lapis/10 px-6 py-4" data-testid={`circle-faq-${i}`}>
              <summary className="cursor-pointer list-none font-serif text-lg text-ink flex justify-between items-center">
                {f.q}<span className="text-lapis transition-transform group-open:rotate-45 text-xl">+</span>
              </summary>
              <p className="mt-3 text-sm text-ink/65 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
        <p className="mt-10 text-center text-xs text-ink/50">
          By joining you agree to the <Link to="/policies/terms" className="underline underline-offset-2">Terms</Link>, <Link to="/policies/privacy" className="underline underline-offset-2">Privacy</Link> & <Link to="/policies/refunds" className="underline underline-offset-2">Refund Policy</Link>.
        </p>
      </section>
    </div>
  );
}
